import { useState, FC, useEffect } from "react";
import styles from "./AdminCustomerDetailsPage.module.scss";

//import { STORE_KEY } from "../constant";
import { toast } from "react-toastify";
import { BounceLoader } from "react-spinners";
import { useDispatch } from "react-redux";
import { ArrowLeft, CopyIcon } from "../../../assets/icons";
import TabSelector from "../../../common/components/tab-selector/TabSelector";
import CommonPagination from "../../../common/components/pagination/CommonPagination";
import { formatDate, handleCopy } from "../../../utils";
import {
	useLazyGetCustomerLoanRepaymentDetailsQuery,
	useLazyGetCustomerDetailQuery,
	useLazyGetCustomerFulfilledLoansQuery,
} from "../../../service/customer-service";
import { hideLoader, showLoader } from "../../../stores/slices/loader-slice";
import ActiveLoanDetails from "./ActiveLoanDetails";
import LoanRepaymentDetails from "./LoanRepaymentDetails";
import FullfilledLoans from "./FulfilledLoans";
import OnboardingStatus from "./OnboardingStatus";
import DebitMandate from "./DebitMandates";
// import { hideLoader, showLoader } from "../../../../stores/slices/loader-slice";
// import ConfirmationDialog from "../../../../common/components/dialog/ConfirmationDialog";

export interface AdminCustomerPageDetailProps {
	onBackPress: () => void;
	customerId: string;
}

const AdminCustomerPageDetails: FC<AdminCustomerPageDetailProps> = ({ onBackPress, customerId }) => {
	const dispatch = useDispatch();
	const [selectedTab, setSelectedTab] = useState(1);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(0);
	const [getCustomerDetail, { data }] = useLazyGetCustomerDetailQuery();
	const [fetchRepaymentDetails, { data: LoanRepaymentData, isLoading: isRepaymentDetailsLoading }] = useLazyGetCustomerLoanRepaymentDetailsQuery(); // Loan repayment
	const [fetchFulfilledLoans, { data: fulfilledLoansData, isLoading: isFulfilledLoansLoading }] = useLazyGetCustomerFulfilledLoansQuery();

	useEffect(() => {
		dispatch(showLoader());
		getCustomerDetail(customerId)
			.unwrap()
			.then()
			.catch(err => toast.error(err?.data?.title as string))
			.finally(() => dispatch(hideLoader()));
	}, []);

	useEffect(() => {
		if (selectedTab === 2) {
			fetchRepaymentDetails({ Page: currentPage, PageSize: 10, customerId })
				.unwrap()
				.then(res => {
					setTotalPages(res?.totalPages);
				})
				.catch(err => toast.error(err?.data?.title as string))
				.finally();
		}
		if (selectedTab === 3) {
			fetchFulfilledLoans({ Page: currentPage, PageSize: 10, customerId })
				.unwrap()
				.then(res => setTotalPages(res?.totalPages))
				.catch(err => toast.error(err?.data?.title as string));
		}
	}, [currentPage, selectedTab]);

	const renderTabContent = () => {
		switch (selectedTab) {
			case 1:
				return <ActiveLoanDetails customerId={customerId} />;
			case 2:
				return <LoanRepaymentDetails data={LoanRepaymentData?.items ?? []} />;
			case 3:
				return <FullfilledLoans data={fulfilledLoansData?.items ?? []} />;
			case 4:
				return <OnboardingStatus customerId={customerId} />;
			case 5:
				return <DebitMandate customerId={customerId} />;
			default:
				return <ActiveLoanDetails customerId={customerId} />;
		}
	};

	const tabs = [
		{ id: 1, title: "Active Loan Details", onClick: () => changeTab(1) },
		{ id: 2, title: "Repayment Details", onClick: () => changeTab(2) },
		{ id: 3, title: "Fulfilled Loan", onClick: () => changeTab(3) },
		{ id: 4, title: "Onboarding Status", onClick: () => changeTab(4) },
		{ id: 5, title: "Debits Mandate", onClick: () => changeTab(5) },
	];

	const changeTab = (tabId: number) => {
		setSelectedTab(tabId);
		setCurrentPage(1);
	};

	return (
		<>
			{/* {showToggleDialog && (
				<ConfirmationDialog
					title={`Are you sure you want to ${data?.isSuspended ? "activate" : "suspend"} this provider ?`}
					description="Kindly confirm before proceeding"
					onCloseDialog={() => setShowToggleDialog(false)}
					onConfirm={() => handleSuspensionToggle()}
					btnText="Proceed"
					actionLoading={suspendingAction}
				/>
			)} */}
			<div className={`${styles.headerSection} ${styles.standardSectionPadding}`}>
				<button style={{ cursor: "pointer" }} onClick={onBackPress}>
					<ArrowLeft />
				</button>
				<div className={styles.text}>Customer Management</div>
			</div>
			<div className={styles.businessSection}>
				<div className={styles.businessNameSection}>
					<span className={styles.businessLabel}>Customer Name</span>
					<span className={styles.breaker}></span>
					<span className={styles.businessName}>
						{data?.firstName} {data?.lastName}
					</span>
				</div>
				<div className={styles.businessInfoSection}>
					<div className={styles.innerSection}>
						<div className={styles.row}>
							<div className={styles.label}>Date of Birth</div>
							<div className={styles.labelValue}>{formatDate(data?.dateOfBirth)}</div>
						</div>
						{data?.phoneNumber && (
							<div className={styles.row}>
								<div className={styles.label}>Phone</div>
								<div className={`${styles.labelValue} ${styles.comboLabel}`}>
									{data?.phoneNumber}
									<CopyIcon className={styles.copyIcon} height={16} onClick={() => handleCopy(data?.phoneNumber)} />
								</div>
							</div>
						)}
						<div className={styles.row}>
							<div className={styles.label}>Email address</div>
							<div className={styles.labelValue}>{data?.email}</div>
						</div>
						<div className={styles.row}>
							<div className={styles.label}>Account Number</div>
							<div className={styles.labelValue}>{data?.accountNumber}</div>
						</div>
						<div className={styles.row}>
							<div className={styles.label}>Amount Paid</div>
							<div className={styles.labelValue}>₦{data?.amountPaid?.toLocaleString()}</div>
						</div>
						<div className={styles.row}>
							<div className={styles.label}>Outstanding Amount</div>
							<div className={styles.labelValue}>₦{data?.outstandingAmount?.toLocaleString()}</div>
						</div>
						<div className={styles.row}>
							<div className={styles.label}>Upcoming Repayment</div>
							<div className={styles.labelValue}>₦{data?.upcomingPayment?.toLocaleString()}</div>
						</div>
						<div className={styles.row}>
							<div className={styles.label}>Status</div>
							<div className={`${styles.labelValue}`}>
								{(() => {
									if (data?.loanStatus?.toLowerCase() === "active") {
										return <span className={`${styles.statusTag} ${styles.active}`}>{data?.loanStatus}</span>;
									} else if (data?.loanStatus?.toLowerCase() === "no ongoing") {
										return <span className={`${styles.statusTag} ${styles.suspended}`}>{data?.loanStatus}</span>;
									} else {
										return <span className={`${styles.statusTag} ${styles.pending}`}>{data?.loanStatus}</span>;
									}
								})()}
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className={styles.tabSelectorWrapper}>
				<div className={styles.tabContainer}>
					<TabSelector tabs={tabs} selectedTab={selectedTab} theme="yellow" />
				</div>
				{renderTabContent()}
				{(selectedTab === 2 || selectedTab === 3) && (
					<div className={styles.paginationContainer}>
						<CommonPagination
							currentPage={currentPage}
							isLoading={isRepaymentDetailsLoading}
							numberOfPages={totalPages}
							onPageChange={(page: number) => {
								setCurrentPage(page);
							}}
						/>
					</div>
				)}
			</div>
		</>
	);
};
export default AdminCustomerPageDetails;
